import { useCreateOrUpdateUserGroupsDialog } from "@/features/management-settings/dialogs/use-create-or-update-user-groups-dialog";
import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination";
import { useUserGroupRepository } from "@/shared/contexts/repository-context";
import { useHasGlobalPermission } from "@/shared/hooks/use-has-permission";
import { getGetUserGroupListPaginatedQueryKey } from "@/spz-api/user-groups/user-groups";
import { useGetMeSuspense } from "@/spz-api/user/user";
import { GlobalPermission } from "@spz/shared-domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserGroupsListColumns } from "../hooks/use-user-groups-list-columns";
const useUserGroupListRows = ({
  search,
  pagination,
}: {
  search?: string;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
}) => {
  const userGroupRepository = useUserGroupRepository();

  const queryParams = {
    search,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };
  const { data: userGroups, isLoading } = useQuery({
    queryKey: getGetUserGroupListPaginatedQueryKey(queryParams),
    queryFn: () => userGroupRepository.getUserGroups(queryParams),
    placeholderData: keepPreviousData,
  });

  return {
    rows: userGroups || [],
    rowCount: userGroups?.length ?? 0,
    isLoading,
  };
};
export function useUserGroupsListViewModel() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const createUserGroupDialog = useCreateOrUpdateUserGroupsDialog();
  const { t } = useTranslation();

  // Check if the user has the UserGroupsManage permission
  const { data: me } = useGetMeSuspense();
  const hasUserGroupsManagementPermission = useHasGlobalPermission(
    GlobalPermission.UserGroupsManage,
    me,
  );

  const { rows, rowCount, isLoading } = useUserGroupListRows({
    search,
    pagination,
  });

  const columns = useUserGroupsListColumns();
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount,
    initialState: {},
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    enableColumnResizing: false,
  });

  const openCreateUserGroupDialog = () => {
    createUserGroupDialog.open();
  };

  const onSearch = useCallback((search: string) => {
    setSearch(search);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  return {
    t,
    hasUserGroupsManagementPermission,
    table,
    onSearch,
    isLoading,
    openCreateUserGroupDialog,
  };
}
